'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery } from 'convex/react'
import { CheckIcon, ChevronUpDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { useToast } from '@/design-system'
import { api } from '../../../convex/_generated/api'
import { Id } from '../../../convex/_generated/dataModel'

/**
 * Partner picker that replaces the partner name/logo in the portal header for a
 * 42a operator.
 *
 * 42a-operator-only, by construction as well as by intent: it renders nothing
 * unless `getPartnerSwitcherContext` says the caller is an `operator_42a`, and
 * the partner list behind the dropdown is gated by `validateOperator`. A real
 * partner user renders the plain PartnerBrandBadge instead and never issues
 * either query.
 *
 * Shown whether or not a session is active. An operator who is ALSO a partner
 * user of their own reaches this portal with no session at all — they still need
 * to jump to another partner from here, which is why this does not key off
 * `getPartnerImpersonationState`.
 *
 * Switching needs no navigation: `startPartnerImpersonation` re-points the
 * operator's session, and because every partner query reads the operator's user
 * row, Convex re-executes them and the whole portal swaps to the new partner in
 * place. Picking your OWN partner ends the session instead of starting a
 * redundant one — same rule OrgSwitcher applies to an operator's own orgs.
 *
 * Deliberately not localized — operator chrome, like the rest of the console.
 */
export default function PartnerSwitcher({
  className,
  currentPartnerId,
  currentName,
  logoUrl,
}: {
  className?: string
  /** The partner the portal is currently resolved to (impersonated or own). */
  currentPartnerId?: string
  currentName?: string
  logoUrl?: string | null
}) {
  const toast = useToast()
  const context = useQuery(api.admin.partnerImpersonation.getPartnerSwitcherContext)
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [switchingTo, setSwitchingTo] = useState<Id<'partners'> | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Only fetch the list once the menu is opened — the header shouldn't pay for
  // it on every portal page load.
  const partners = useQuery(
    api.admin.partnerImpersonation.listPartnersForImpersonation,
    isOpen ? {} : 'skip',
  )
  const startPartnerImpersonation = useMutation(
    api.admin.partnerImpersonation.startPartnerImpersonation,
  )
  const stopPartnerImpersonation = useMutation(
    api.admin.partnerImpersonation.stopPartnerImpersonation,
  )

  useEffect(() => {
    if (!isOpen) return
    const onClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    document.addEventListener('keydown', onEscape)
    return () => {
      document.removeEventListener('mousedown', onClickOutside)
      document.removeEventListener('keydown', onEscape)
    }
  }, [isOpen])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return partners ?? []
    return (partners ?? []).filter(
      (p) => p.name.toLowerCase().includes(q) || p.slug.toLowerCase().includes(q),
    )
  }, [partners, search])

  // Not a 42a operator → the header keeps its normal partner badge.
  if (!context) return null

  const handleSelect = async (partnerId: Id<'partners'>) => {
    if (partnerId === currentPartnerId) {
      setIsOpen(false)
      return
    }
    setSwitchingTo(partnerId)
    try {
      if (partnerId === context.ownPartnerId) {
        // Back to the operator's own partner: end the session rather than
        // impersonating yourself, which would leave a session open forever.
        await stopPartnerImpersonation({})
      } else {
        await startPartnerImpersonation({ partnerId })
      }
      // No navigation: the portal's queries re-run against the new partner.
      setIsOpen(false)
      setSearch('')
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      toast.danger('Could not switch partner', message)
    } finally {
      setSwitchingTo(null)
    }
  }

  return (
    <div ref={containerRef} className={`relative ${className ?? ''}`}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        title="Switch the partner whose portal you are viewing"
        className="flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-sm font-medium transition-colors hover:bg-[var(--color-surface-hover)]"
        style={{
          // Tinted only while impersonating, so an operator sitting in their own
          // partner's portal still sees ordinary chrome.
          borderColor: context.isImpersonating ? 'var(--color-purple)' : 'var(--color-border)',
          background: context.isImpersonating ? 'var(--color-purple-muted)' : 'transparent',
          color: 'var(--color-text-primary)',
        }}
      >
        {logoUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={logoUrl} alt="" className="h-5 w-auto max-w-[90px] object-contain" />
        ) : null}
        <span className="max-w-[160px] truncate">{currentName ?? 'Select partner'}</span>
        <ChevronUpDownIcon
          className="h-4 w-4 flex-shrink-0"
          style={{ color: context.isImpersonating ? 'var(--color-purple)' : 'var(--color-text-muted)' }}
        />
      </button>

      {isOpen && (
        <div
          className="absolute end-0 z-50 mt-1 w-72 overflow-hidden rounded-xl border shadow-lg"
          style={{ background: 'var(--color-surface)', borderColor: 'var(--color-border)' }}
        >
          <div className="border-b p-2" style={{ borderColor: 'var(--color-border)' }}>
            <div className="relative">
              <MagnifyingGlassIcon
                className="pointer-events-none absolute start-2 top-1/2 h-4 w-4 -translate-y-1/2"
                style={{ color: 'var(--color-text-muted)' }}
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search partners..."
                autoFocus
                className="w-full rounded-lg py-1.5 pe-2 ps-8 text-sm outline-none"
                style={{
                  background: 'var(--color-background)',
                  color: 'var(--color-text-primary)',
                  border: '1px solid var(--color-border)',
                }}
              />
            </div>
          </div>

          <div className="max-h-72 overflow-y-auto py-1" role="listbox">
            {partners === undefined ? (
              <p className="px-3 py-2 text-sm" style={{ color: 'var(--color-text-muted)' }}>
                Loading partners...
              </p>
            ) : filtered.length === 0 ? (
              <p className="px-3 py-2 text-sm" style={{ color: 'var(--color-text-muted)' }}>
                No partners match &ldquo;{search.trim()}&rdquo;.
              </p>
            ) : (
              filtered.map((partner) => {
                const isCurrent = partner._id === currentPartnerId
                const isOwn = partner._id === context.ownPartnerId
                return (
                  <button
                    key={partner._id}
                    type="button"
                    role="option"
                    aria-selected={isCurrent}
                    onClick={() => void handleSelect(partner._id)}
                    disabled={switchingTo !== null}
                    className="flex w-full items-center gap-2 px-3 py-2 text-start text-sm transition-colors hover:bg-[var(--color-surface-hover)] disabled:opacity-60"
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    <CheckIcon
                      className="h-4 w-4 flex-shrink-0"
                      style={{ color: isCurrent ? 'var(--color-purple)' : 'transparent' }}
                    />
                    <span className="min-w-0 flex-1 truncate">{partner.name}</span>
                    {isOwn && !isCurrent && (
                      <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                        yours
                      </span>
                    )}
                    {!partner.isActive && (
                      <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                        inactive
                      </span>
                    )}
                    {switchingTo === partner._id && (
                      <span
                        className="h-3.5 w-3.5 flex-shrink-0 animate-spin rounded-full border-2 border-t-transparent"
                        style={{ borderColor: 'var(--color-purple)', borderTopColor: 'transparent' }}
                      />
                    )}
                  </button>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}
